import autoTable from 'jspdf-autotable';
import { COLORS } from './constants';
import { formatNumber, calcPercentChange, getReportPeriod } from './utils';
import { drawHeader, drawPageNumber, drawTitleUnderline, drawFooterLine, drawLogo } from './layout';

// PAGE 1: Title page
export const buildTitlePage = async (doc, report, margin, pageWidth, pageHeight) => {
  await drawHeader(doc, report, margin, pageWidth);
  
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(32);
  doc.setFont('Karla', 'bold');
  doc.text('RAPPORT', margin, 65);
  doc.text('CONSOMMATION', margin, 80);
  
  doc.setTextColor(...COLORS.azur);
  doc.text('ENERGETIQUE', margin, 95);
  
  drawTitleUnderline(doc, margin, 102, 70);
  
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(24);
  doc.setFont('Karla', 'bold');
  doc.text((report.client_name || 'Client').toUpperCase(), margin, 130);
  
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(14);
  doc.setFont('Karla', 'normal');
  const periodInfo = getReportPeriod(report);
  doc.text(`${periodInfo.month} ${periodInfo.year}`, margin, 145);
  
  const siteCount = report.sites?.length || 0;
  doc.setFontSize(12);
  doc.text(`${siteCount} sites analyses`, margin, 160);
  
  drawFooterLine(doc, margin, pageWidth, pageHeight);
  
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(8);
  const dateStr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  doc.text(`Genere le ${dateStr}`, margin, pageHeight - 12);
};

// PAGE 2: Key figures (KPIs)
export const buildKPIsPage = async (doc, report, sites, margin, pageWidth, pageHeight) => {
  await drawHeader(doc, report, margin, pageWidth);
  drawPageNumber(doc, 2, pageHeight);
  
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(26);
  doc.setFont('Karla', 'bold');
  doc.text('CHIFFRES', margin, 55);
  const chiffresWidth = doc.getTextWidth('CHIFFRES');
  doc.setTextColor(...COLORS.azur);
  doc.text('CLES', margin + chiffresWidth + 4, 55);
  drawTitleUnderline(doc, margin, 60, 50);
  
  const periodInfo = getReportPeriod(report);
  const reportMonth = periodInfo.month;
  const reportYear = periodInfo.year;
  const prevYear = parseInt(reportYear) - 1;
  
  const boxWidth = 55;
  const boxHeight = 50;
  const startY = 80;
  const gap = 8;
  
  // KPI 1: Sites actifs
  doc.setFillColor(...COLORS.blancLunaire);
  doc.roundedRect(margin, startY, boxWidth, boxHeight, 4, 4, 'F');
  doc.setDrawColor(...COLORS.argent);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, startY, boxWidth, boxHeight, 4, 4, 'S');
  
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(9);
  doc.setFont('Karla', 'normal');
  doc.text('SITES ACTIFS', margin + 5, startY + 12);
  
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(36);
  doc.setFont('Karla', 'bold');
  doc.text(`${sites?.length || 0}`, margin + 5, startY + 38);
  
  // KPI 2: Consommation du mois
  let currentMonth = Number(report.totals?.current_month_mwh) || 0;
  if (currentMonth === 0 && sites?.length > 0) {
    currentMonth = sites.reduce((sum, s) => sum + (Number(s.current_mwh) || Number(s.total_mwh) || 0), 0);
  }
  
  doc.setFillColor(...COLORS.blancLunaire);
  doc.roundedRect(margin + boxWidth + gap, startY, boxWidth, boxHeight, 4, 4, 'F');
  doc.setDrawColor(...COLORS.azur);
  doc.setLineWidth(1);
  doc.roundedRect(margin + boxWidth + gap, startY, boxWidth, boxHeight, 4, 4, 'S');
  
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(8);
  doc.setFont('Karla', 'normal');
  doc.text('CONSOMMATION', margin + boxWidth + gap + 5, startY + 10);
  doc.text(reportMonth.toUpperCase(), margin + boxWidth + gap + 5, startY + 17);
  
  doc.setTextColor(...COLORS.azur);
  doc.setFontSize(26);
  doc.setFont('Karla', 'bold');
  doc.text(`${formatNumber(currentMonth)}`, margin + boxWidth + gap + 5, startY + 35);
  doc.setFontSize(12);
  doc.text('MWh', margin + boxWidth + gap + 5, startY + 46);
  
  // KPI 3: Cumul a date
  const monthlyData = report.monthly_data?.current_year || {};
  const previousYearMonthly = report.monthly_data?.previous_year || {};
  const monthNames = ['', 'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 
                      'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
  const monthNamesLower = ['', 'janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 
                           'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'];
  
  let reportMonthNum = parseInt(report.report_month_num || '0');
  if (reportMonthNum === 0 && report.report_month) {
    const monthStr = report.report_month.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').split(' ')[0];
    const foundIndex = monthNamesLower.findIndex(m => m && monthStr.startsWith(m.substring(0, 3)));
    if (foundIndex > 0) reportMonthNum = foundIndex;
  }
  if (reportMonthNum === 0) {
    const monthsWithData = Object.entries(monthlyData)
      .filter(([, d]) => (d?.measured_mwh || 0) > 0)
      .map(([k]) => parseInt(k));
    if (monthsWithData.length > 0) {
      reportMonthNum = Math.max(...monthsWithData);
    } else {
      const anyData = Object.entries(monthlyData)
        .filter(([, d]) => (d?.consumption_mwh || 0) > 0)
        .map(([k]) => parseInt(k));
      reportMonthNum = anyData.length > 0 ? Math.max(...anyData) : 1;
    }
  }
  
  let cumulTotal = 0;
  let lastMonthWithData = reportMonthNum;
  for (let m = 1; m <= 12; m++) {
    const mk = m.toString().padStart(2, '0');
    const md = monthlyData[mk] || {};
    const measured = Number(md.measured_mwh || 0);
    const estimated = Number(md.estimated_mwh || 0);
    if (measured > 0) {
      cumulTotal += measured;
      lastMonthWithData = m;
    } else if (estimated > 0 && m <= reportMonthNum) {
      cumulTotal += estimated;
      lastMonthWithData = m;
    }
  }
  
  const ytdMonthName = monthNames[lastMonthWithData] || monthNames[reportMonthNum];
  let currentYearYTD = cumulTotal > 0 ? cumulTotal : Number(report.totals?.current_year_mwh) || 0;
  
  let previousYearSamePeriod = 0;
  for (let m = 1; m <= lastMonthWithData; m++) {
    const mk = m.toString().padStart(2, '0');
    previousYearSamePeriod += Number((previousYearMonthly[mk] || {}).consumption_mwh || 0);
  }
  if (previousYearSamePeriod === 0) {
    const prevTotal = Number(report.totals?.previous_year_mwh) || 0;
    if (prevTotal > 0 && lastMonthWithData > 0) {
      previousYearSamePeriod = Math.round((lastMonthWithData / 12) * prevTotal);
    }
  }
  
  doc.setFillColor(...COLORS.blancLunaire);
  doc.roundedRect(margin + 2*(boxWidth + gap), startY, boxWidth, boxHeight, 4, 4, 'F');
  doc.setDrawColor(...COLORS.argent);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin + 2*(boxWidth + gap), startY, boxWidth, boxHeight, 4, 4, 'S');
  
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(8);
  doc.setFont('Karla', 'normal');
  doc.text('CUMUL', margin + 2*(boxWidth + gap) + 5, startY + 10);
  doc.text('A DATE', margin + 2*(boxWidth + gap) + 5, startY + 17);
  
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(26);
  doc.setFont('Karla', 'bold');
  doc.text(`${formatNumber(currentYearYTD)}`, margin + 2*(boxWidth + gap) + 5, startY + 35);
  doc.setFontSize(12);
  doc.text('MWh', margin + 2*(boxWidth + gap) + 5, startY + 46);
  
  // Evolution vs N-1
  let previousMonth = Number(report.totals?.previous_month_mwh) || 0;
  if (previousMonth === 0 && sites?.length > 0) {
    previousMonth = sites.reduce((sum, s) => sum + (Number(s.previous_month_mwh) || Number(s.previous_mwh) || 0), 0);
  }
  
  const monthChange = calcPercentChange(currentMonth, previousMonth);
  
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(14);
  doc.setFont('Karla', 'bold');
  doc.text('EVOLUTION VS N-1', margin, startY + 75);
  
  if (monthChange !== null && previousMonth > 0) {
    const isGood = monthChange < 0;
    const color = isGood ? COLORS.emeraude : COLORS.ambre;
    const bgColor = isGood ? COLORS.emeraudeLight : COLORS.ambreLight;
    
    doc.setFillColor(...bgColor);
    doc.roundedRect(margin, startY + 82, 85, 28, 4, 4, 'F');
    doc.setDrawColor(...color);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, startY + 82, 85, 28, 4, 4, 'S');
    
    doc.setFillColor(...color);
    const arrowX = margin + 12, arrowY = startY + 96;
    if (isGood) {
      doc.triangle(arrowX - 4, arrowY - 4, arrowX + 4, arrowY - 4, arrowX, arrowY + 4, 'F');
    } else {
      doc.triangle(arrowX - 4, arrowY + 4, arrowX + 4, arrowY + 4, arrowX, arrowY - 4, 'F');
    }
    
    doc.setTextColor(...color);
    doc.setFontSize(20);
    doc.setFont('Karla', 'bold');
    const sign = monthChange > 0 ? '+' : '';
    doc.text(`${sign}${monthChange.toFixed(1)}%`, margin + 22, startY + 100);
    
    doc.setTextColor(...COLORS.grisAcier);
    doc.setFontSize(9);
    doc.setFont('Karla', 'normal');
    doc.text(`vs ${reportMonth} ${prevYear}`, margin, startY + 118);
    doc.text(`(${formatNumber(previousMonth)} MWh)`, margin, startY + 126);
  } else {
    doc.setTextColor(...COLORS.grisAcier);
    doc.setFontSize(10);
    doc.setFont('Karla', 'normal');
    doc.text('Donnees N-1 non disponibles', margin, startY + 95);
  }
  
  // YTD comparison
  const ytdX = margin + 95;
  const ytdChange = calcPercentChange(currentYearYTD, previousYearSamePeriod);
  
  if (currentYearYTD > 0) {
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(14);
    doc.setFont('Karla', 'bold');
    doc.text('CUMUL A DATE', ytdX, startY + 75);
    
    if (ytdChange !== null && previousYearSamePeriod > 0) {
      const isYtdGood = ytdChange < 0;
      const ytdColor = isYtdGood ? COLORS.emeraude : COLORS.ambre;
      const ytdBgColor = isYtdGood ? COLORS.emeraudeLight : COLORS.ambreLight;
      
      doc.setFillColor(...ytdBgColor);
      doc.roundedRect(ytdX, startY + 82, 75, 28, 4, 4, 'F');
      doc.setDrawColor(...ytdColor);
      doc.setLineWidth(0.5);
      doc.roundedRect(ytdX, startY + 82, 75, 28, 4, 4, 'S');
      
      doc.setFillColor(...ytdColor);
      const ax = ytdX + 12, ay = startY + 96;
      if (isYtdGood) {
        doc.triangle(ax - 4, ay - 4, ax + 4, ay - 4, ax, ay + 4, 'F');
      } else {
        doc.triangle(ax - 4, ay + 4, ax + 4, ay + 4, ax, ay - 4, 'F');
      }
      
      doc.setTextColor(...ytdColor);
      doc.setFontSize(18);
      doc.setFont('Karla', 'bold');
      const ytdSign = ytdChange > 0 ? '+' : '';
      doc.text(`${ytdSign}${ytdChange.toFixed(1)}%`, ytdX + 22, startY + 100);
    }
    
    doc.setTextColor(...COLORS.grisAcier);
    doc.setFontSize(9);
    doc.setFont('Karla', 'normal');
    doc.text(`Fin ${ytdMonthName} ${prevYear}`, ytdX, startY + 118);
    doc.text(`${formatNumber(previousYearSamePeriod)} MWh`, ytdX, startY + 126);
  }
  
  drawFooterLine(doc, margin, pageWidth, pageHeight);
};

// PAGE 3: Top sites chart
export const buildChartPage = async (doc, report, sites, margin, pageWidth, pageHeight) => {
  await drawHeader(doc, report, margin, pageWidth);
  drawPageNumber(doc, 3, pageHeight);
  
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(26);
  doc.setFont('Karla', 'bold');
  doc.text('CONSOMMATION', margin, 55);
  const cw = doc.getTextWidth('CONSOMMATION');
  doc.setTextColor(...COLORS.azur);
  doc.text('PAR SITE', margin + cw + 4, 55);
  drawTitleUnderline(doc, margin, 60, 80);
  
  const periodInfo = getReportPeriod(report);
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(11);
  doc.setFont('Karla', 'normal');
  doc.text(`Top 15 sites - ${periodInfo.month} ${periodInfo.year}`, margin, 72);
  
  const sortedSites = [...(sites || [])].sort((a, b) => 
    (b.total_mwh || b.current_mwh || 0) - (a.total_mwh || a.current_mwh || 0)
  ).slice(0, 15);
  
  if (sortedSites.length === 0) {
    doc.setTextColor(...COLORS.grisAcier);
    doc.text('Aucune donnee disponible', margin, 100);
    return;
  }
  
  const chartStartY = 85;
  const barHeight = 10;
  const barGap = 2;
  const maxValue = Math.max(...sortedSites.map(s => s.total_mwh || s.current_mwh || 0));
  const chartWidth = pageWidth - margin * 2 - 85;
  
  sortedSites.forEach((site, i) => {
    const y = chartStartY + i * (barHeight + barGap);
    const value = site.total_mwh || site.current_mwh || 0;
    const barWidth = (value / maxValue) * chartWidth * 0.75;
    
    doc.setTextColor(...COLORS.bleuPetroleDark);
    doc.setFontSize(7);
    doc.setFont('Karla', 'normal');
    const name = site.name || '-';
    const displayName = name.length > 35 ? name.substring(0, 32) + '...' : name;
    doc.text(displayName, margin, y + barHeight * 0.7);
    
    doc.setFillColor(...COLORS.azur);
    doc.roundedRect(margin + 70, y, barWidth, barHeight - 1, 1, 1, 'F');
    
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(7);
    doc.setFont('Karla', 'bold');
    doc.text(`${formatNumber(value)} MWh`, margin + 72 + barWidth, y + barHeight * 0.7);
  });
  
  drawFooterLine(doc, margin, pageWidth, pageHeight);
};

// PAGE 4: Top 5 sections
export const buildTop5Page = async (doc, report, sites, margin, pageWidth, pageHeight) => {
  await drawHeader(doc, report, margin, pageWidth);
  drawPageNumber(doc, 4, pageHeight);
  
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(26);
  doc.setFont('Karla', 'bold');
  doc.text('ANALYSE DES', margin, 55);
  const adw = doc.getTextWidth('ANALYSE DES');
  doc.setTextColor(...COLORS.azur);
  doc.text('PERFORMANCES', margin + adw + 4, 55);
  drawTitleUnderline(doc, margin, 60, 95);
  
  const sortedSites = [...(sites || [])].sort((a, b) => 
    (a.total_mwh || a.current_mwh || 0) - (b.total_mwh || b.current_mwh || 0)
  );
  
  const top5 = sortedSites.slice(0, 5);
  const flop5 = sortedSites.slice(-5).reverse();
  const boxWidth = 82;
  const startY = 78;
  
  // TOP 5 ECONOMES
  doc.setTextColor(...COLORS.azur);
  doc.setFontSize(13);
  doc.setFont('Karla', 'bold');
  doc.text('TOP 5 - ECONOMES', margin, startY);
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(9);
  doc.setFont('Karla', 'normal');
  doc.text('Sites les plus economes en energie (MWh)', margin, startY + 8);
  
  doc.setFillColor(...COLORS.blancLunaire);
  doc.roundedRect(margin, startY + 14, boxWidth, 75, 4, 4, 'F');
  doc.setDrawColor(...COLORS.azur);
  doc.setLineWidth(0.8);
  doc.roundedRect(margin, startY + 14, boxWidth, 75, 4, 4, 'S');
  
  top5.forEach((site, i) => {
    const y = startY + 26 + i * 14;
    doc.setFillColor(...COLORS.azur);
    doc.circle(margin + 8, y, 4.5, 'F');
    doc.setTextColor(...COLORS.white);
    doc.setFontSize(9);
    doc.setFont('Karla', 'bold');
    doc.text(`${i + 1}`, margin + 8, y + 1.8, { align: 'center' });
    
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(8);
    doc.setFont('Karla', 'normal');
    const name = (site.name || '-');
    doc.text(name.length > 20 ? name.substring(0, 17) + '...' : name, margin + 16, y + 1.5);
    
    doc.setTextColor(...COLORS.azur);
    doc.setFont('Karla', 'bold');
    doc.setFontSize(7);
    doc.text(`${formatNumber(site.total_mwh || site.current_mwh)} MWh`, margin + boxWidth - 5, y + 1.5, { align: 'right' });
  });
  
  // TOP 5 ENERGIVORES
  const rightX = margin + boxWidth + 12;
  doc.setTextColor(...COLORS.bleuPetroleDark);
  doc.setFontSize(13);
  doc.setFont('Karla', 'bold');
  doc.text('TOP 5 - ENERGIVORES', rightX, startY);
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(9);
  doc.setFont('Karla', 'normal');
  doc.text('Sites energivores a surveiller (MWh)', rightX, startY + 8);
  
  doc.setFillColor(...COLORS.blancLunaire);
  doc.roundedRect(rightX, startY + 14, boxWidth, 75, 4, 4, 'F');
  doc.setDrawColor(...COLORS.bleuPetroleDark);
  doc.setLineWidth(0.8);
  doc.roundedRect(rightX, startY + 14, boxWidth, 75, 4, 4, 'S');
  
  flop5.forEach((site, i) => {
    const y = startY + 26 + i * 14;
    doc.setFillColor(...COLORS.bleuPetroleDark);
    doc.circle(rightX + 8, y, 4.5, 'F');
    doc.setTextColor(...COLORS.white);
    doc.setFontSize(9);
    doc.setFont('Karla', 'bold');
    doc.text(`${i + 1}`, rightX + 8, y + 1.8, { align: 'center' });
    
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(8);
    doc.setFont('Karla', 'normal');
    const name = (site.name || '-');
    doc.text(name.length > 20 ? name.substring(0, 17) + '...' : name, rightX + 16, y + 1.5);
    
    doc.setTextColor(...COLORS.bleuPetroleDark);
    doc.setFont('Karla', 'bold');
    doc.setFontSize(7);
    doc.text(`${formatNumber(site.total_mwh || site.current_mwh)} MWh`, rightX + boxWidth - 5, y + 1.5, { align: 'right' });
  });
  
  drawFooterLine(doc, margin, pageWidth, pageHeight);
};

// PAGE 5: Sites table
export const buildSitesTablePage = async (doc, report, sites, margin, pageWidth, pageHeight) => {
  await drawHeader(doc, report, margin, pageWidth);
  drawPageNumber(doc, 5, pageHeight);
  
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(26);
  doc.setFont('Karla', 'bold');
  doc.text('DETAIL', margin, 55);
  const dw = doc.getTextWidth('DETAIL');
  doc.setTextColor(...COLORS.azur);
  doc.text('PAR SITE', margin + dw + 4, 55);
  drawTitleUnderline(doc, margin, 60, 50);
  
  const periodInfo = getReportPeriod(report);
  const reportMonth = periodInfo.month;
  const reportYear = periodInfo.year;
  const prevYear = parseInt(reportYear) - 1;
  
  const sortedSites = [...(sites || [])].sort((a, b) => 
    (b.total_mwh || b.current_mwh || 0) - (a.total_mwh || a.current_mwh || 0)
  );
  
  const allTableData = sortedSites.map((site, i) => {
    const currentRaw = Number(site.total_mwh || site.current_month_mwh || site.current_mwh) || 0;
    const previousRaw = Number(site.previous_month_mwh || site.previous_mwh) || 0;
    const current = Math.round(currentRaw * 10) / 10;
    const previous = Math.round(previousRaw * 10) / 10;
    const change = calcPercentChange(current, previous);
    
    return [
      (i + 1).toString(),
      site.name || '-',
      `${formatNumber(current)} MWh`,
      previous > 0 ? `${previous.toFixed(1)} MWh` : '-',
      change !== null ? `${change > 0 ? '+' : ''}${change.toFixed(1)}%` : '-'
    ];
  });
  
  const sitesPerPage = 25;
  const totalPages = Math.ceil(allTableData.length / sitesPerPage);
  
  for (let page = 0; page < totalPages; page++) {
    if (page > 0) {
      doc.addPage();
      await drawHeader(doc, report, margin, pageWidth);
      drawPageNumber(doc, 5 + page, pageHeight);
      
      doc.setTextColor(...COLORS.anthracite);
      doc.setFontSize(22);
      doc.setFont('Karla', 'bold');
      doc.text('DETAIL', margin, 55);
      const dw2 = doc.getTextWidth('DETAIL');
      doc.setTextColor(...COLORS.azur);
      doc.text(`PAR SITE (suite ${page + 1}/${totalPages})`, margin + dw2 + 4, 55);
      drawTitleUnderline(doc, margin, 60, 80);
    }
    
    const startIdx = page * sitesPerPage;
    const tableData = allTableData.slice(startIdx, startIdx + sitesPerPage);
    
    autoTable(doc, {
      startY: page === 0 ? 75 : 70,
      head: [['#', 'Site', `${reportMonth} ${reportYear}`, `${reportMonth} ${prevYear}`, 'Evol.']],
      body: tableData,
      theme: 'plain',
      styles: { font: 'Karla', fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
      headStyles: { fillColor: COLORS.azur, textColor: COLORS.white, fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { textColor: COLORS.anthracite },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center', textColor: COLORS.grisAcier },
        1: { cellWidth: 'auto', minCellWidth: 50 },
        2: { cellWidth: 30, halign: 'right', fontStyle: 'bold', textColor: COLORS.azur },
        3: { cellWidth: 30, halign: 'right', textColor: COLORS.grisAcier },
        4: { cellWidth: 18, halign: 'center' },
      },
      alternateRowStyles: { fillColor: COLORS.blancLunaire },
      margin: { left: margin, right: margin },
      tableWidth: 'auto',
      didParseCell: (data) => {
        if (data.column.index === 4 && data.section === 'body') {
          const val = parseFloat(data.cell.raw);
          if (!isNaN(val)) {
            data.cell.styles.textColor = val < 0 ? COLORS.emeraude : COLORS.ambre;
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
    });
    
    drawFooterLine(doc, margin, pageWidth, pageHeight);
  }
};

// PAGE 6: Analysis
export const buildAnalysisPage = async (doc, report, margin, pageWidth, pageHeight) => {
  if (!report.energy_manager_analysis) return false;
  
  await drawHeader(doc, report, margin, pageWidth);
  drawPageNumber(doc, 6, pageHeight);
  
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(26);
  doc.setFont('Karla', 'bold');
  doc.text('ANALYSE', margin, 55);
  const aw = doc.getTextWidth('ANALYSE');
  doc.setTextColor(...COLORS.azur);
  doc.text('ENERGY MANAGER', margin + aw + 4, 55);
  drawTitleUnderline(doc, margin, 60, 100);
  
  const cleanTextForPDF = (text) => {
    return text
      .replace(/ < /g, ' inferieur a ')
      .replace(/ > /g, ' superieur a ')
      .replace(/<(\d)/g, 'inferieur a $1')
      .replace(/>(\d)/g, 'superieur a $1')
      .replace(/≤/g, 'inferieur ou egal a ')
      .replace(/≥/g, 'superieur ou egal a ')
      .replace(/–/g, '-').replace(/—/g, '-')
      .replace(/'/g, "'").replace(/'/g, "'")
      .replace(/"/g, '"').replace(/"/g, '"')
      .replace(/…/g, '...')
      .replace(/\u00A0/g, ' ')
      .replace(/  +/g, ' ');
  };
  
  const rawText = cleanTextForPDF(report.energy_manager_analysis);
  const availableWidth = pageWidth - (margin * 2);
  const textStartX = margin + 8;
  const maxWidth = availableWidth - 13;
  
  doc.setFontSize(9);
  doc.setFont('Karla', 'normal');
  const lines = doc.splitTextToSize(rawText, maxWidth);
  
  const lineHeight = 4.0;
  const startY = 82;
  const endY = pageHeight - 25;
  const maxLinesPerPage = Math.floor((endY - startY) / lineHeight);
  const displayLines = lines.slice(0, maxLinesPerPage);
  
  doc.setFillColor(...COLORS.blancLunaire);
  const boxHeight = Math.min(displayLines.length * lineHeight + 16, pageHeight - 90);
  doc.roundedRect(margin, 70, availableWidth, boxHeight, 4, 4, 'F');
  
  doc.setFillColor(...COLORS.azur);
  doc.rect(margin, 70, 4, boxHeight, 'F');
  
  let yPos = startY;
  for (let i = 0; i < displayLines.length && yPos < endY; i++) {
    let line = displayLines[i];
    const isHeader = /^(\d+\.\s|[A-Z][A-Z\s]+:?$|Vue d'ensemble|Analyse|Synthese|Recommandations|Points d'attention|Tendances|Evolution)/i.test(line.trim());
    const isBullet = /^\s*[-•]\s/.test(line) || /^\s*\d+\.\s/.test(line);
    
    if (isHeader && line.trim().length > 0) {
      if (i > 0) yPos += 2;
      line = line.replace(/\*\*/g, '').replace(/#{1,3}\s*/g, '').trim();
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.azur);
      doc.setFont('Karla', 'bold');
      doc.text(line, textStartX, yPos);
      yPos += lineHeight + 1;
    } else if (isBullet) {
      line = line.replace(/^\s*[-•]\s*/, '  - ').replace(/\*\*/g, '');
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.anthracite);
      doc.setFont('Karla', 'normal');
      doc.text(line, textStartX + 2, yPos);
      yPos += lineHeight;
    } else {
      line = line.replace(/\*\*/g, '');
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.anthracite);
      doc.setFont('Karla', 'normal');
      doc.text(line, textStartX, yPos);
      yPos += lineHeight;
    }
  }
  
  drawFooterLine(doc, margin, pageWidth, pageHeight);
  return true;
};

// FINAL PAGE: Closing
export const buildClosingPage = async (doc, report, margin, pageWidth, pageHeight) => {
  const centerX = pageWidth / 2;
  const logoHeight = 22;
  const logoWidth = logoHeight * 2.755;
  await drawLogo(doc, centerX - (logoWidth / 2), 60, 1.8);
  
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(14);
  doc.setFont('Karla', 'normal');
  doc.text('Votre partenaire en efficacite energetique', centerX, 110, { align: 'center' });
  
  doc.setDrawColor(...COLORS.argent);
  doc.setLineWidth(0.5);
  doc.line(centerX - 50, 120, centerX + 50, 120);
  
  const contact = report.contact_info || {};
  const hasContact = contact.name || contact.email || contact.phone || contact.agency;
  
  if (hasContact) {
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(12);
    doc.setFont('Karla', 'bold');
    doc.text('Votre contact Place des Energies', centerX, 135, { align: 'center' });
    
    let yPos = 148;
    doc.setFontSize(11);
    doc.setFont('Karla', 'normal');
    doc.setTextColor(...COLORS.bleuPetroleDark);
    
    if (contact.name) {
      doc.setFont('Karla', 'bold');
      doc.text(contact.name, centerX, yPos, { align: 'center' });
      yPos += 10;
      doc.setFont('Karla', 'normal');
    }
    if (contact.title) {
      doc.setTextColor(...COLORS.grisAcier);
      doc.text(contact.title, centerX, yPos, { align: 'center' });
      yPos += 10;
      doc.setTextColor(...COLORS.bleuPetroleDark);
    }
    if (contact.agency) { doc.text(contact.agency, centerX, yPos, { align: 'center' }); yPos += 10; }
    if (contact.phone) { doc.text(contact.phone, centerX, yPos, { align: 'center' }); yPos += 10; }
    if (contact.email) {
      doc.setTextColor(...COLORS.azur);
      doc.text(contact.email, centerX, yPos, { align: 'center' });
      yPos += 10;
    }
    if (contact.address) {
      doc.setTextColor(...COLORS.grisAcier);
      doc.setFontSize(9);
      doc.text(contact.address, centerX, yPos, { align: 'center' });
    }
  } else {
    doc.setTextColor(...COLORS.bleuPetroleDark);
    doc.setFontSize(11);
    doc.text('www.placedesenergies.com', centerX, 140, { align: 'center' });
    doc.text('contact@placedesenergies.com', centerX, 152, { align: 'center' });
  }
  
  doc.setTextColor(...COLORS.azur);
  doc.setFontSize(16);
  doc.setFont('Karla', 'bold');
  doc.text('Merci de votre confiance', centerX, 220, { align: 'center' });
  
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(9);
  doc.setFont('Karla', 'normal');
  doc.text('www.placedesenergies.com', centerX, pageHeight - 15, { align: 'center' });
};
